import { useState, useEffect, useContext } from "react";
import { Box, Card, Grid, Stack } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import moment from "moment";

import useAuth 			from "../../../hooks/useAuth";
import useFetch			from '../../../api/api'
import CompanyContext	from "../context";
import config 			from "../../../common/Config";
import {formatCurrency} from '../../../common/Helper'

import Loading 			from "../../../components/Loading";
import IndicatorCard 	from "./IndicatorCard";
import RestrictedAccess from "../../../components/RestrictedAccess";

const TabDividends = () => {

	const colors = ['#FFC0CB','#FF8C00','#BA55D3','#32CD32','#40E0D0','#00BFFF','#696969','#F4A460']
	const {stocks, selectedStock, indicators, currency, isNotesOpen} = useContext(CompanyContext)

	const {auth}										= useAuth()
	const privateFetch 									= useFetch()

	const [dividends, setDividends] 					= useState([])
	const [userDividends, setUserDividends]				= useState([])
	const [dividendsByYear, setDividendsByYear]			= useState([])
	const [isLoading, setIsLoading]						= useState(true)
	const [isLogged, setIsLogged]						= useState(auth.userId ? true : false)

	useEffect(() => {
		setIsLogged(auth.userId ? true : false)
	}, [auth])
	
	useEffect(() => {

		if (!stocks || !selectedStock) { return }

		// Recupera os dividendos pagos para o usuario
		if (isLogged) {
			privateFetch(config.apiUrl +'/api/portfolio/historicDividends/'+ selectedStock)
			.then((data) => data.json())
			.then((data) => {
				setUserDividends(data)
			})
		}

		const stockDividends = stocks.find(s => s.ticker === selectedStock)?.dividends || []
		setDividends(stockDividends)

		// Sumariza os dividos por ano e se o ativo estiver no portfolio, 
		// calcula o valor pago para cada registro
		let yearlyDividends = []

		for (let d of stockDividends) {
			let index = yearlyDividends.findIndex(yd => yd.date === new Date(d.date).getFullYear())

			if (index >= 0) {
				if (yearlyDividends[index]) {
					yearlyDividends[index].dividend 		+= parseFloat(d.value)
					yearlyDividends[index].adjustedDividend	+= parseFloat(d.adjustedValue)
				}
				else {
					yearlyDividends[index].dividend 		= parseFloat(d.value)
					yearlyDividends[index].adjustedDividend	= parseFloat(d.adjustedValue)
				}
			}
			else {
				if (new Date(d.date).getFullYear() !== 1969) {
					yearlyDividends.push({
						date 				: new Date(d.date).getFullYear(),
						dividend			: parseFloat(d.value),
						adjustedDividend	: parseFloat(d.adjustedValue)
					})
				}
			}
		}

		// Arredonda os valores
		yearlyDividends.map(yd => {
			Object.keys(yd).map(key => {
				switch(key) {
					case 'dividend' 		: yd[key] = parseFloat(yd[key]).toFixed(2); break;
					case 'adjustedDividend'	: yd[key] = parseFloat(yd[key]).toFixed(4); break;
				}
			})
		})

		yearlyDividends.sort((a,b) => a.date - b.date)
		setDividendsByYear(yearlyDividends)
		setIsLoading(false)

	}, [selectedStock, stocks, isLogged])

	if (isLoading) return (<Loading/>)

	const columns = [
		{ 
			field : 'date', 
			headerName : 'Data Pagto',
			headerAlign : 'center',
			renderCell: (cellValues) => moment(cellValues.row.date).isValid() ? moment(cellValues.row.date).format("DD/MM/YY") : "Sem Data",
			// width : 60,
			align: 'center',
			sortable : false, 
			disableColumnMenu : true
		},			
		{ 
			field : 'dateEx', 
			headerName : 'Data Ex',
			headerAlign : 'center',
			renderCell: (cellValues) => moment(cellValues.row.dateEx).format("DD/MM/YY"),
			// width : 60,
			align: 'center',
			sortable : false, 
			disableColumnMenu : true
		},			
		{ field : 'type', headerName : 'Tipo', headerAlign : 'center', align : 'center', width : 50, sortable : false, disableColumnMenu : true},
		{ 
			field : 'value', 
			headerName : 'Valor',
			headerAlign : 'center',			
			renderCell: (cellValues) => formatCurrency(cellValues.row.value, 'SMALL', currency == 'BRL' ? 'BRL' : 'USD'),
			// width : 50,
			align: 'center',
			sortable : false, 
			disableColumnMenu : true
		},
		{
			field : 'valueReceived',
			headerName : 'Proventos',
			renderCell : (cellValues) => {
				let div = userDividends.find(pd => pd.data_pagto === cellValues.row.date && pd.type === cellValues.row.type)
				return div?.earnings ? formatCurrency(parseFloat(div.earnings), 'SMALL', currency == 'BRL' ? 'BRL' : 'USD') : '-'
			},
			// width : 80,
			sortable : false, 
			disableColumnMenu : true
		}
	]

	return (
		
		<Box id="dividends">

				<Grid container spacing={1}>
					<Grid item xs={12} md={isNotesOpen ? 12 : 6} lg={isNotesOpen ? 6 : 4}>
						<Card variant="outlined" sx={{padding:1, height: '97%'}} >
							<DataGrid
								getRowId={(row) => row.id} 
								rows={ dividends }
								columns={columns}
								initialState={{
									pagination: { paginationModel: { pageSize: 15 } },
								}}				
								pageSizeOptions={[5, 15, 20]}
								rowHeight={30}
								slots={{
									noRowsOverlay: () => (
										<Box display={'flex'} flexGrow={1} justifyContent={'center'} alignItems={'center'} height={'100%'} width={'100%'}>
											<Box display={'flex'} width={'95%'} justifyContent={'center'} alignItems={'center'}>
												{auth.userId ? (
													<Box>Essa empresa ainda não distribuiu dividendos</Box>
												) : (
													<RestrictedAccess message={'Faça login para visualizar os dividendos pagos por essa empresa.'}/>
												)}
											</Box>
										</Box>
									)
								}}											
							/>
						</Card>
					</Grid>
					<Grid item xs={12} md={isNotesOpen ? 12 : 6} lg={isNotesOpen ? 6 : 8}>
						<Stack>
							<Grid container spacing={1}>
								<Grid item xs={12} lg={isNotesOpen ? 6 : 3}>
									<IndicatorCard data={indicators} size={isNotesOpen ? "xs" : "sm"} indicator={'dividendYield'} height={90}/>
								</Grid>
								<Grid item xs={12} lg={isNotesOpen ? 6 : 3}>
									<IndicatorCard data={indicators} size={isNotesOpen ? "xs" : "sm"} indicator={'payout'} height={90}/>
								</Grid>							
								<Grid item xs={12} lg={isNotesOpen ? 6 : 3}>
									<IndicatorCard data={indicators} size={isNotesOpen ? "xs" : "sm"} indicator={'dividendCagr5y'} height={90}/>
								</Grid>
								<Grid item xs={12} lg={isNotesOpen ? 6 : 3}>
									<IndicatorCard data={indicators} size={isNotesOpen ? "xs" : "sm"} indicator={'dividendCagr10y'} height={90}/>
								</Grid>
							</Grid>

							{dividendsByYear ? (
								<Card variant="outlined" sx={{marginTop:1}}>
									<ResponsiveContainer width={'100%'} height={500}>
										{ auth.userId ? (
											<BarChart
												width={1000}
												height={400}
												data={(dividendsByYear ? dividendsByYear : null)}
												margin={{
													top: 5,
													right: 30,
													left: 20,
													bottom: 5,
												}}
												>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis style={{fontSize: 10}} dataKey="date" />
												<YAxis style={{fontSize: 10}} />
												<Tooltip formatter={(value => formatCurrency(value, 'SMALL', currency == 'BRL' ? 'BRL' : 'USD'))}/>
												<Legend wrapperStyle={{fontSize: "12px"}} iconType='circle' iconSize={8}/>
												<Bar key='dividend' name='Dividendos' dataKey='dividend' fill={colors[1]}/>
											</BarChart>		
										) : (
											<Box display={'flex'} justifyContent={'center'} alignItems={'center'} width={1000} height={400}>
												<RestrictedAccess message={'Faça login para visualizar o gráfico de dividendos.'}/>
											</Box>
										)}	
									</ResponsiveContainer>
								</Card>
							):(
								<h1>loading</h1>
							)}
						</Stack>
					</Grid>
				</Grid>
			
		</Box>
	)		


}

export default TabDividends